import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import {
  DEFAULT_MAP_CENTER,
  dedupePropertiesByAddress,
  isBlockedPropertyAddress,
  isFiniteCoordinate,
  loadCachedProperties,
  sanitizePropertyForMap,
  saveCachedProperties,
} from "../utils/propertyUtils";
import { getPublicAreaSummary, normalizeAreaTags } from "../utils/profileUtils";

const FAVORITE_PROPERTIES_STORAGE_KEY = "rentduo_favorite_properties_v1";

export default function usePropertyData({
  isMapLoaded = false,
  isMobile = false,
  role = "tenant",
  preferredAreasText = "",
  serviceAreasText = "",
  city = "",
  profile = null,
  user = null,
  setStatusMessage = null,
} = {}) {
  const [properties, setProperties] = useState(() => loadCachedProperties());
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showMobilePropertyModal, setShowMobilePropertyModal] = useState(false);
  const [showClaimVerificationModal, setShowClaimVerificationModal] = useState(false);
  const [propertyClaimTarget, setPropertyClaimTarget] = useState(null);
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [pendingPropertyLocation, setPendingPropertyLocation] = useState(null);
  const [propertyMapCenter, setPropertyMapCenter] = useState(DEFAULT_MAP_CENTER);
  const [propertyMapZoom, setPropertyMapZoom] = useState(() =>
    window.innerWidth <= 768 ? 14 : 12
  );
  const [hasUserMapLocation, setHasUserMapLocation] = useState(false);
  const [favoritePropertyIds, setFavoritePropertyIds] = useState(() => {
    try {
      const raw = window.localStorage.getItem(FAVORITE_PROPERTIES_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Favorite properties load error:", error);
      return [];
    }
  });

  const visibleProperties = useMemo(
    () =>
      dedupePropertiesByAddress(properties).filter(
        (item) => item?.propertyStatus !== "deleted"
      ),
    [properties]
  );

  useEffect(() => {
    if (!isMapLoaded || !window.google?.maps) return;

    let cancelled = false;

    const applyPreferredCenter = (lat, lng) => {
      if (cancelled) return;
      if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
      setPropertyMapCenter({ lat: Number(lat), lng: Number(lng) });
      setPropertyMapZoom(isMobile ? 14 : 13);
      setHasUserMapLocation(true);
    };

    const tryDeviceLocation = () => {
      if (!navigator.geolocation) return;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (cancelled) return;

          const nextCenter = {
            lat: Number(position.coords.latitude),
            lng: Number(position.coords.longitude),
          };

          if (Number.isFinite(nextCenter.lat) && Number.isFinite(nextCenter.lng)) {
            setPropertyMapCenter(nextCenter);
            setPropertyMapZoom(isMobile ? 15 : 14);
            setHasUserMapLocation(true);
          }
        },
        (error) => {
          console.log("Geolocation unavailable:", error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000,
        }
      );
    };

    const roleAreas = normalizeAreaTags(
      role === "tenant"
        ? preferredAreasText || profile?.preferredAreasText || ""
        : serviceAreasText || profile?.serviceAreasText || ""
    );

    const profileLocationCandidates = [
      [city || profile?.city, profile?.state].filter(Boolean).join(", "),
      city,
      profile?.city,
      profile?.state,
      roleAreas[0],
      getPublicAreaSummary(profile || {}),
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    const preferredLocationLabel = Array.from(new Set(profileLocationCandidates))[0];

    if (!preferredLocationLabel) {
      tryDeviceLocation();
      return () => {
        cancelled = true;
      };
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: preferredLocationLabel }, (results, status) => {
      if (cancelled) return;

      const location = results?.[0]?.geometry?.location;
      if (status === "OK" && location) {
        applyPreferredCenter(location.lat(), location.lng());
        return;
      }

      tryDeviceLocation();
    });

    return () => {
      cancelled = true;
    };
  }, [
    isMapLoaded,
    isMobile,
    city,
    profile?.city,
    profile?.state,
    preferredAreasText,
    serviceAreasText,
    role,
    profile,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadPropertiesOnce = async () => {
      try {
        const snap = await getDocs(collection(db, "properties"));
        if (!isMounted) return;

        const list = snap.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .filter((item) => item.propertyStatus !== "deleted")
          .map((item) => sanitizePropertyForMap(item))
          .filter((item) => isFiniteCoordinate(item.lat) && isFiniteCoordinate(item.lng));

        list.sort((a, b) => {
          const aTime =
            a.createdAt?.seconds ||
            (typeof a.createdAt === "string"
              ? new Date(a.createdAt).getTime() / 1000
              : 0);
          const bTime =
            b.createdAt?.seconds ||
            (typeof b.createdAt === "string"
              ? new Date(b.createdAt).getTime() / 1000
              : 0);
          return bTime - aTime;
        });

        if (list.length) {
          setProperties((prev) => {
            const localPending = prev.filter(
              (item) =>
                item?.isLocalPending === true &&
                !list.some((dbItem) => dbItem.id === item.id)
            );
            return dedupePropertiesByAddress([...localPending, ...list]);
          });
        }
      } catch (error) {
        console.error("Initial properties load error:", error);
      }
    };

    loadPropertiesOnce();

    const unsub = onSnapshot(
      collection(db, "properties"),
      (snapshot) => {
        const list = snapshot.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .filter((item) => item.propertyStatus !== "deleted")
          .map((item) => sanitizePropertyForMap(item))
          .filter((item) => isFiniteCoordinate(item.lat) && isFiniteCoordinate(item.lng));

        list.sort((a, b) => {
          const aTime =
            a.createdAt?.seconds ||
            (typeof a.createdAt === "string"
              ? new Date(a.createdAt).getTime() / 1000
              : 0);
          const bTime =
            b.createdAt?.seconds ||
            (typeof b.createdAt === "string"
              ? new Date(b.createdAt).getTime() / 1000
              : 0);
          return bTime - aTime;
        });

        setProperties((prev) => {
          const localPending = prev.filter(
            (item) =>
              item?.isLocalPending === true &&
              !list.some((dbItem) => dbItem.id === item.id)
          );

          if (!list.length && localPending.length) {
            return dedupePropertiesByAddress(localPending);
          }

          return dedupePropertiesByAddress([...localPending, ...list]);
        });
      },
      (error) => {
        console.error("Properties listener error:", error);
        if (user && typeof setStatusMessage === "function") {
          setStatusMessage(
            "Property sync failed. Showing saved local pins. Check Firestore rules if new pins still do not sync."
          );
        }
      }
    );

    return () => {
      isMounted = false;
      unsub();
    };
  }, [user, setStatusMessage]);

  useEffect(() => {
    const cacheable = properties
      .filter((item) => item && isFiniteCoordinate(item.lat) && isFiniteCoordinate(item.lng))
      .map((item) => ({
        ...item,
        createdAt: item.createdAt?.seconds
          ? { seconds: item.createdAt.seconds }
          : item.createdAt instanceof Date
          ? item.createdAt.toISOString()
          : item.createdAt || null,
        updatedAt: item.updatedAt?.seconds
          ? { seconds: item.updatedAt.seconds }
          : item.updatedAt instanceof Date
          ? item.updatedAt.toISOString()
          : item.updatedAt || null,
      }));

    saveCachedProperties(cacheable);
  }, [properties]);

  useEffect(() => {
    const cleaned = dedupePropertiesByAddress(properties);
    if (cleaned.length !== properties.length) {
      setProperties(cleaned);
      return;
    }

    if (
      selectedProperty &&
      isBlockedPropertyAddress(
        selectedProperty?.fullAddress ||
          selectedProperty?.address ||
          selectedProperty?.streetLine ||
          selectedProperty?.title ||
          ""
      )
    ) {
      setSelectedProperty(null);
    }

    if (
      hoveredProperty &&
      isBlockedPropertyAddress(
        hoveredProperty?.fullAddress ||
          hoveredProperty?.address ||
          hoveredProperty?.streetLine ||
          hoveredProperty?.title ||
          ""
      )
    ) {
      setHoveredProperty(null);
    }
  }, [properties, selectedProperty, hoveredProperty]);

  useEffect(() => {
    const validProperties = visibleProperties.filter(
      (item) => item && isFiniteCoordinate(item.lat) && isFiniteCoordinate(item.lng)
    );

    if (
      selectedProperty?.lat != null &&
      selectedProperty?.lng != null &&
      isFiniteCoordinate(selectedProperty.lat) &&
      isFiniteCoordinate(selectedProperty.lng)
    ) {
      setPropertyMapCenter({
        lat: Number(selectedProperty.lat),
        lng: Number(selectedProperty.lng),
      });
      setPropertyMapZoom(15);
      return;
    }

    if (hasUserMapLocation) return;
    if (!validProperties.length) return;

    const firstProperty = validProperties[0];
    setPropertyMapCenter({
      lat: Number(firstProperty.lat),
      lng: Number(firstProperty.lng),
    });
    setPropertyMapZoom(isMobile ? 13 : 12);
  }, [properties, selectedProperty, hasUserMapLocation, isMobile, visibleProperties]);

  useEffect(() => {
    if (!isMapLoaded || !properties.length) return;

    const timer = window.setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 500);

    return () => window.clearTimeout(timer);
  }, [properties, isMapLoaded]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        FAVORITE_PROPERTIES_STORAGE_KEY,
        JSON.stringify(favoritePropertyIds)
      );
    } catch (error) {
      console.error("Favorite properties save error:", error);
    }
  }, [favoritePropertyIds]);

  const isFavoriteProperty = (propertyId = "") => favoritePropertyIds.includes(propertyId);

  const toggleFavoriteProperty = (propertyId = "") => {
    if (!propertyId) return;
    setFavoritePropertyIds((prev) =>
      prev.includes(propertyId)
        ? prev.filter((item) => item !== propertyId)
        : [propertyId, ...prev]
    );
  };

  return {
    properties,
    setProperties,
    selectedProperty,
    setSelectedProperty,
    showMobilePropertyModal,
    setShowMobilePropertyModal,
    showClaimVerificationModal,
    setShowClaimVerificationModal,
    propertyClaimTarget,
    setPropertyClaimTarget,
    hoveredProperty,
    setHoveredProperty,
    pendingPropertyLocation,
    setPendingPropertyLocation,
    propertyMapCenter,
    setPropertyMapCenter,
    propertyMapZoom,
    setPropertyMapZoom,
    hasUserMapLocation,
    setHasUserMapLocation,
    favoritePropertyIds,
    setFavoritePropertyIds,
    visibleProperties,
    isFavoriteProperty,
    toggleFavoriteProperty,
  };
}
